import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ChevronLeft, ChevronRight, ChevronRight as ChevronRightIcon, Archive } from "lucide-react";
import { searchPatients, getRaisonsArchivage, archiverPatient, type RaisonArchivage } from "../../../api/professionalPatient";
import { getEtablissementPatients, type EtablissementPatientSummary } from "../../../api/etablissement";
import { useAuth } from "../../../store/authEtablissementStore";
import { formatFrenchDate } from "../../../lib/patientSummary";
import ConsultationsBadge from "../../../components/patients/ConsultationsBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

type PatientRow = {
  nip: string;
  nom: string;
  prenom: string;
  email: string;
  nombreConsultations?: number;
  derniereConsultation?: string;
};

const PATIENT_NIP_OK = /^\d{10}$/;
const DEFAULT_PAGE_SIZE = 20;
const SEARCH_PAGE_SIZE = 10;

function flattenRow(o: Record<string, unknown>): Record<string, unknown> {
  for (const key of ["patient", "dossier", "patientInfo"] as const) {
    const n = o[key];
    if (n && typeof n === "object" && !Array.isArray(n)) return { ...(n as Record<string, unknown>), ...o };
  }
  return o;
}

function pickNip(m: Record<string, unknown>): string {
  return String(m.nip ?? m.patientNip ?? m.anip ?? m.numeroNational ?? "").trim();
}

function mapSearchRow(r: unknown): PatientRow {
  if (!r || typeof r !== "object") return { nip: "", nom: "", prenom: "", email: "" };
  const m = flattenRow(r as Record<string, unknown>);
  return {
    nip: pickNip(m),
    nom: String(m.nom ?? "").toUpperCase(),
    prenom: String(m.prenom ?? ""),
    email: String(m.email ?? ""),
  };
}

function mapDefaultRow(r: EtablissementPatientSummary): PatientRow {
  return {
    nip: r.nip,
    nom: (r.nom ?? "").toUpperCase(),
    prenom: r.prenom ?? "",
    email: r.email ?? "",
    nombreConsultations: r.nombreConsultations,
    derniereConsultation: r.derniereConsultation,
  };
}

export default function EtablissementPatients() {
  const navigate = useNavigate();
  const { establishment } = useAuth();

  // ── NIP 10 cases (5 × 2 chiffres) ────────────────────────────────────────
  const [nip, setNip] = useState("");
  const nipPartRefs = useRef<Array<HTMLInputElement | null>>([]);
  const nipValid = useMemo(() => PATIENT_NIP_OK.test(nip), [nip]);
  const nipParts = useMemo(
    () => [nip.slice(0, 2), nip.slice(2, 4), nip.slice(4, 6), nip.slice(6, 8), nip.slice(8, 10)],
    [nip],
  );

  const updateNipPart = (index: number, rawValue: string) => {
    const digits = rawValue.replace(/\D/g, "").slice(0, 2);
    const parts = [...nipParts];
    parts[index] = digits;
    setNip(parts.join("").slice(0, 10));
    if (digits.length === 2 && index < 4) {
      requestAnimationFrame(() => nipPartRefs.current[index + 1]?.focus());
    }
  };

  const handleNipPartKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    if (event.key === "Backspace" && input.value.length === 0 && index > 0) {
      requestAnimationFrame(() => {
        const prev = nipPartRefs.current[index - 1];
        if (!prev) return;
        prev.focus();
        try { prev.setSelectionRange(prev.value.length, prev.value.length); } catch { /* ignore */ }
      });
    }
  };

  const handleNipPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 10);
    if (!pasted) return;
    event.preventDefault();
    setNip(pasted);
    const nextIndex = Math.min(4, Math.floor((pasted.length - 1) / 2));
    requestAnimationFrame(() => nipPartRefs.current[nextIndex]?.focus());
  };

  // ── Recherche par NIP exact (complément — ne remplace pas la liste par défaut) ─
  const [searchRows, setSearchRows] = useState<PatientRow[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchPage, setSearchPage] = useState(1);

  const search = useCallback(async (nipValue: string) => {
    setSearchLoading(true);
    setSearchError(null);
    const res = await searchPatients({ nip: nipValue });
    setSearchLoading(false);
    if (!res.ok) {
      setSearchError(res.message);
      setSearchRows([]);
      return;
    }
    setSearchRows((res.data as unknown[]).map(mapSearchRow));
    setSearchPage(1);
  }, []);

  // Auto-recherche dès que les 10 chiffres sont saisis
  useEffect(() => {
    if (nipValid) {
      void search(nip);
    } else {
      setSearchRows([]);
      setSearchError(null);
    }
  }, [nip, nipValid, search]);

  // ── Liste par défaut : patients ayant eu au moins une consultation ici ──────
  const etablissementId = establishment?.id?.trim();
  const [defaultRows, setDefaultRows] = useState<PatientRow[]>([]);
  const [defaultLoading, setDefaultLoading] = useState(true);
  const [defaultError, setDefaultError] = useState<string | null>(null);
  const [defaultPage, setDefaultPage] = useState(1);
  const [defaultTotal, setDefaultTotal] = useState(0);
  const [defaultTotalPages, setDefaultTotalPages] = useState(1);

  const loadDefault = useCallback(async (targetPage: number) => {
    if (!etablissementId) {
      setDefaultError("Établissement non identifié — reconnectez-vous.");
      setDefaultRows([]);
      setDefaultLoading(false);
      return;
    }
    setDefaultLoading(true);
    setDefaultError(null);
    const res = await getEtablissementPatients(etablissementId, targetPage, DEFAULT_PAGE_SIZE);
    setDefaultLoading(false);
    if (!res.ok) {
      setDefaultError(res.message);
      setDefaultRows([]);
      return;
    }
    setDefaultRows(res.data.map(mapDefaultRow));
    setDefaultTotal(res.pagination.total);
    setDefaultTotalPages(Math.max(1, res.pagination.totalPages));
  }, [etablissementId]);

  // Recherche par NIP active dès qu'au moins un chiffre est saisi (masque la liste par défaut).
  const isSearching = nip.length > 0;

  useEffect(() => {
    if (!isSearching) void loadDefault(defaultPage);
  }, [isSearching, defaultPage, loadDefault]);

  const [confirmNip, setConfirmNip] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [raisons, setRaisons] = useState<RaisonArchivage[]>([]);
  const [loadingRaisons, setLoadingRaisons] = useState(false);
  const [raisonsError, setRaisonsError] = useState<string | null>(null);
  const [selectedRaisonId, setSelectedRaisonId] = useState("");
  const [motifLibre, setMotifLibre] = useState("");

  // ── Vue affichée : résultats de recherche NIP, ou liste par défaut ──────────
  const loading = isSearching ? searchLoading : defaultLoading;
  const error = isSearching ? searchError : defaultError;
  const page = isSearching ? searchPage : defaultPage;
  const setPage = isSearching ? setSearchPage : setDefaultPage;

  const paged = useMemo(() => {
    if (isSearching) {
      const start = (searchPage - 1) * SEARCH_PAGE_SIZE;
      return { rows: searchRows.slice(start, start + SEARCH_PAGE_SIZE), total: searchRows.length };
    }
    return { rows: defaultRows, total: defaultTotal };
  }, [isSearching, searchRows, searchPage, defaultRows, defaultTotal]);
  const totalPages = isSearching
    ? Math.max(1, Math.ceil(paged.total / SEARCH_PAGE_SIZE))
    : defaultTotalPages;

  function openArchiveModal(nip: string) {
    setConfirmNip(nip);
    setArchiveError(null);
    setRaisonsError(null);
    setSelectedRaisonId("");
    setMotifLibre("");
    setRaisons([]);
    setLoadingRaisons(true);
    void getRaisonsArchivage().then((res) => {
      if (res.ok) {
        setRaisons(res.data);
      } else {
        setRaisonsError("Impossible de charger les raisons d'archivage.");
      }
    }).catch(() => {
      setRaisonsError("Erreur réseau lors du chargement des raisons.");
    }).finally(() => setLoadingRaisons(false));
  }

  const canSubmitArchive = !!(selectedRaisonId || motifLibre.trim());

  const handleArchive = async () => {
    if (!confirmNip || !canSubmitArchive) return;
    const etablissementId = establishment?.id;
    if (!etablissementId) { setArchiveError("Établissement non identifié."); return; }
    setArchiving(true);
    setArchiveError(null);
    const res = await archiverPatient(confirmNip, {
      etablissementId,
      raisonArchivageId: selectedRaisonId || undefined,
      motifLibre: motifLibre.trim() || undefined,
    });
    setArchiving(false);
    if (!res.ok) {
      const msg = res.status === 409
        ? "Ce patient est déjà archivé dans votre établissement."
        : res.status === 403
          ? "Opération réservée aux établissements de santé ou administrateurs."
          : res.message;
      setArchiveError(msg);
      return;
    }
    setConfirmNip(null);
    setSearchRows((prev) => prev.filter((r) => r.nip !== confirmNip));
    setDefaultRows((prev) => prev.filter((r) => r.nip !== confirmNip));
  };

  return (
    <div>
      <h1 className="text-3xl md:text-4xl font-bold text-[#0B1F16]">Gestion des patients</h1>

      <div className="mt-6 flex flex-wrap items-end gap-3 justify-between">
        {/* ── 10 cases NIP ── */}
        <div>
          <p className="mb-2 text-sm text-[#0B1F16]/60">Saisissez l'ANIP en 5 groupes de 2 chiffres.</p>
          <div className="flex flex-wrap items-center gap-2">
            {nipParts.map((part, index) => (
              <div key={`nip-${index}`} className="flex items-center gap-2">
                <input
                  ref={(el) => { nipPartRefs.current[index] = el; }}
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={2}
                  value={part}
                  onChange={(e) => updateNipPart(index, e.target.value)}
                  onKeyDown={(e) => handleNipPartKeyDown(index, e)}
                  onPaste={handleNipPaste}
                  aria-label={`NIP groupe ${index + 1}`}
                  aria-invalid={nip.length > 0 && !nipValid}
                  className="h-12 w-14 text-center rounded-[10px] border bg-white text-[#0B1F16] outline-none focus:ring-2 focus:ring-[#00553E]/30"
                  style={{ borderColor: nipValid || nip === "" ? "#E5E7EB" : "#F87171" }}
                />
                {index < 4 && (
                  <span className="text-[#0B1F16]/30 font-bold select-none">–</span>
                )}
              </div>
            ))}
            {nip.length > 0 && (
              <button
                type="button"
                onClick={() => setNip("")}
                className="h-12 px-4 rounded-[10px] border text-sm text-[#0B1F16]/60 hover:bg-gray-50"
                style={{ borderColor: "#E5E7EB" }}
              >
                Effacer
              </button>
            )}
          </div>
        </div>

        <button
          className="h-12 px-6 rounded-[10px] text-white font-semibold inline-flex items-center gap-2"
          style={{ background: "#00553E" }}
          onClick={() => navigate("/etablissement/dashboard/patients/nouveau")}
        >
          <Plus size={16} /> Ajouter un patient
        </button>
      </div>

      {error && (
        <div className="mt-3 rounded-[10px] border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-900">
          {error}
        </div>
      )}

      <div className="mt-4 bg-white rounded-[12px] border" style={{ borderColor: "#E5E7EB" }}>
        <div className="overflow-x-auto">
          <table className="min-w-[640px] w-full text-left text-sm">
            <thead>
              <tr className="border-b text-[#0B1F16]" style={{ borderColor: "#E5E7EB" }}>
                <th className="px-5 py-3 font-semibold">NIP</th>
                <th className="px-5 py-3 font-semibold">NOM</th>
                <th className="px-5 py-3 font-semibold">PRÉNOM</th>
                <th className="px-5 py-3 font-semibold">E-MAIL</th>
                <th className="px-5 py-3 font-semibold">CONSULTATIONS</th>
                <th className="px-5 py-3 font-semibold">DERNIÈRE CONSULTATION</th>
                <th className="px-5 py-3" />
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-5 py-10 text-center text-[#0B1F16]/60">{isSearching ? "Recherche…" : "Chargement…"}</td></tr>
              ) : isSearching && !nipValid ? (
                <tr><td colSpan={8} className="px-5 py-10 text-center text-[#0B1F16]/40">{10 - nip.length} chiffre(s) restant(s)…</td></tr>
              ) : isSearching && paged.rows.length === 0 ? (
                <tr><td colSpan={8} className="px-5 py-10 text-center text-[#0B1F16]/60">Aucun patient trouvé pour ce NIP.</td></tr>
              ) : !isSearching && paged.rows.length === 0 ? (
                <tr><td colSpan={8} className="px-5 py-10 text-center text-[#0B1F16]/60">Aucun patient n'a encore consulté dans cet établissement.</td></tr>
              ) : (
                paged.rows.map((row) => (
                  <tr
                    key={row.nip || row.email}
                    className={`border-t ${PATIENT_NIP_OK.test(row.nip) ? "hover:bg-gray-50 cursor-pointer" : ""}`}
                    style={{ borderColor: "#F3F4F6" }}
                    onClick={() => {
                      if (!PATIENT_NIP_OK.test(row.nip)) return;
                      navigate(`/etablissement/dashboard/patients/${encodeURIComponent(row.nip)}`);
                    }}
                  >
                    <td className="px-5 py-3 whitespace-nowrap font-mono text-xs">{row.nip}</td>
                    <td className="px-5 py-3 whitespace-nowrap">{row.nom}</td>
                    <td className="px-5 py-3 whitespace-nowrap">{row.prenom}</td>
                    <td className="px-5 py-3 whitespace-nowrap text-gray-500">{row.email || "—"}</td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <ConsultationsBadge count={row.nombreConsultations ?? 0} />
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-gray-500">
                      {formatFrenchDate(row.derniereConsultation)}
                    </td>
                    <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {PATIENT_NIP_OK.test(row.nip) && (
                        <button
                          type="button"
                          title="Archiver ce patient"
                          onClick={() => openArchiveModal(row.nip)}
                          className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800"
                        >
                          <Archive size={14} /> Archiver
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-3 text-gray-400">
                      {PATIENT_NIP_OK.test(row.nip) && <ChevronRightIcon size={14} />}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-4 text-sm text-[#0B1F16]/70">
          <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="disabled:opacity-40">
            <ChevronLeft size={16} />
          </button>
          <span>{page} / {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} className="disabled:opacity-40">
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {confirmNip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => !archiving && setConfirmNip(null)}>
          <div className="w-full max-w-sm rounded-[12px] bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            {/* En-tête */}
            <div className="border-b px-6 py-4" style={{ borderColor: "#E5E7EB" }}>
              <div className="text-base font-bold text-[#0B1F16]">Archiver le patient</div>
            </div>

            {/* Corps */}
            <div className="space-y-4 px-6 py-5">
              <p className="font-mono text-sm text-gray-500">NIP : {confirmNip}</p>
              <p className="rounded-[10px] border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
                Cette action archive le dossier. Le professionnel ne pourra plus y accéder sans réactivation.
              </p>

              {/* SELECT raisons */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500">
                  Raison d'archivage
                </label>
                {loadingRaisons ? (
                  <div className="h-10 animate-pulse rounded-md bg-gray-100" />
                ) : raisonsError ? (
                  <div className="flex items-center justify-between rounded-md border border-orange-200 bg-orange-50 px-3 py-2">
                    <span className="text-xs text-orange-800">{raisonsError}</span>
                    <button
                      type="button"
                      onClick={() => openArchiveModal(confirmNip!)}
                      className="ml-3 text-xs font-medium text-orange-700 underline hover:no-underline"
                    >
                      Réessayer
                    </button>
                  </div>
                ) : (
                  <Select value={selectedRaisonId} onValueChange={setSelectedRaisonId}>
                    <SelectTrigger>
                      <SelectValue placeholder="— Sélectionner une raison —" />
                    </SelectTrigger>
                    <SelectContent>
                      {raisons.map((r) => (
                        <SelectItem key={r._id} value={r._id}>
                          {r.libelle}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Motif libre */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-500">
                  Motif libre <span className="text-gray-400">(optionnel si raison choisie)</span>
                </label>
                <textarea
                  value={motifLibre}
                  onChange={(e) => setMotifLibre(e.target.value)}
                  rows={3}
                  placeholder="Précision contextuelle…"
                  className="w-full resize-none rounded-[8px] border px-3 py-2 text-sm text-[#0B1F16] outline-none focus:border-[#00553E]"
                  style={{ borderColor: "#D1D5DB" }}
                />
                {!canSubmitArchive && (
                  <p className="mt-1 text-xs text-red-500">Au moins une raison ou un motif libre est requis.</p>
                )}
              </div>

              {archiveError && (
                <div className="rounded-[8px] border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
                  {archiveError}
                </div>
              )}
            </div>

            {/* Pied */}
            <div className="flex justify-end gap-3 border-t px-6 py-4" style={{ borderColor: "#E5E7EB" }}>
              <button
                type="button"
                disabled={archiving}
                onClick={() => setConfirmNip(null)}
                className="h-9 px-4 rounded-[10px] border text-sm font-medium text-[#0B1F16] disabled:opacity-50"
                style={{ borderColor: "#E5E7EB" }}
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={!canSubmitArchive || archiving}
                onClick={() => void handleArchive()}
                className="h-9 px-4 rounded-[10px] text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: canSubmitArchive && !archiving ? "#B91C1C" : "#D1D5DB" }}
              >
                {archiving ? "Archivage…" : "Confirmer l'archivage"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
